import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Crown,
  Zap,
  Star,
  Shield,
  CreditCard,
  ExternalLink,
  Gift,
  Building,
  Sparkles,
  Coins,
  Loader2,
} from 'lucide-react';
import { Alert, LoadingSpinner } from '@/components/ui';
import { SpotlightCard } from '@/components/verdict/SpotlightCard';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import type { SubscriptionStatus, Tier } from '@/types';
import { useCredits } from '@/hooks/useCredits';

const tierIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  trial: Gift, analyst: Zap, partner: Star, team: Crown,
  basic: Zap, standard: Star, professional: Crown,
  master: Shield, enterprise: Building,
};

const tierLabels: Record<string, string> = {
  master: 'Master', enterprise: 'Enterprise', trial: 'Standard User',
  analyst: 'Analyst', partner: 'Partner', team: 'Team',
  basic: 'Analyst', standard: 'Partner', professional: 'Team',
};

const TIER_HIERARCHY: Record<string, number> = {
  trial: 0, analyst: 1, partner: 2, team: 3,
  basic: 1, standard: 2, professional: 3,
  enterprise: 4, master: 5,
};

const TIER_CREDIT_INFO: Record<string, { credits: string; rolloverCap: string; creditsPerDollar: number; isUnlimited?: boolean }> = {
  analyst: { credits: '1,350', rolloverCap: '2,700', creditsPerDollar: 150 },
  partner: { credits: '4,600', rolloverCap: '9,200', creditsPerDollar: 159 },
  team: { credits: 'Unlimited', rolloverCap: 'N/A', creditsPerDollar: 0, isUnlimited: true },
  basic: { credits: '1,350', rolloverCap: '2,700', creditsPerDollar: 150 },
  standard: { credits: '4,600', rolloverCap: '9,200', creditsPerDollar: 159 },
  professional: { credits: 'Unlimited', rolloverCap: 'N/A', creditsPerDollar: 0, isUnlimited: true },
};

const BASE_CREDITS_PER_DOLLAR = 100;

function TierDisplay({ tier, className }: { tier: string; className?: string }) {
  const Icon = tierIcons[tier] || Star;
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Icon className="w-4 h-4" />
      <span>{tierLabels[tier] || tier}</span>
    </span>
  );
}

export function PlansPage() {
  useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [currentTier, setCurrentTier] = useState<Tier | null>(null);
  const [availableTiers, setAvailableTiers] = useState<Tier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [purchasingPack, setPurchasingPack] = useState<string | null>(null);

  const { balance, isUnlimited, packs, fetchPacks, purchaseCredits } = useCredits();

  // Packs are lazy-loaded — fetch them when this page mounts
  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subData, tierData, tiersData] = await Promise.all([
          api.getSubscriptionStatus(),
          api.getMyTier(),
          api.getTiers(),
        ]);
        setSubscription(subData);
        setCurrentTier(tierData.current_tier);
        setAvailableTiers(tiersData.tiers.filter((t: Tier) => t.tier !== 'trial'));
        setError(null);
      } catch (err) {
        console.error('Failed to load plans data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load plans');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCheckoutLoading(null);
        setPurchasingPack(null);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleSelectPlan = (tierName: string) => {
    const period = billingPeriod === 'monthly' ? 'monthly' : 'annual';
    navigate(`/checkout?tier=${tierName}&period=${period}`);
  };

  const handleBuyCredits = async (packId: string) => {
    if (purchasingPack) return; // prevent double-clicks
    setPurchasingPack(packId);
    setMessage(null);
    try {
      const checkoutUrl = await purchaseCredits(packId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setMessage({ type: 'error', text: 'Failed to create checkout session. Please try again.' });
        setPurchasingPack(null);
      }
    } catch {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
      setPurchasingPack(null);
    }
  };

  const handleManageSubscription = async () => {
    setCheckoutLoading('manage');
    try {
      const result = await api.createBillingPortalSession();
      window.location.href = result.portal_url;
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to open billing portal'
      });
      setCheckoutLoading(null);
    }
  };

  const isMasterOrEnterprise = currentTier?.tier === 'master' || currentTier?.tier === 'enterprise';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8 animate-fade-in">
        <Alert variant="error">{error}</Alert>
        <button
          onClick={() => window.location.reload()}
          className="px-5 py-2.5 rounded-xl text-sm font-medium bg-surface-light border border-border-subtle text-text-primary hover:bg-surface-light hover:text-text-primary transition-all"
        >
          Retry
        </button>
      </div>
    );
  }

  const formatPrice = (tier: Tier) => {
    if (!tier.pricing) return { price: '$0', period: '', originalPrice: null, annualTotal: null };
    if (billingPeriod === 'annual') {
      const annualPrice = tier.pricing.monthly * 10;
      const monthlyEquivalent = annualPrice / 12;
      return {
        price: `$${monthlyEquivalent.toFixed(2)}`, period: '/mo',
        originalPrice: `$${tier.pricing.monthly.toFixed(2)}`,
        annualTotal: `$${annualPrice.toFixed(0)}/year billed annually`
      };
    }
    return { price: `$${tier.pricing.monthly.toFixed(2)}`, period: '/mo', originalPrice: null, annualTotal: null };
  };

  return (
    <div className="space-y-10 animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="relative text-center">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-verdict-primary/8 blur-[100px] rounded-full pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-verdict-primary/10 border border-verdict-primary/20 rounded-full mb-4">
            <Coins className="w-3.5 h-3.5 text-verdict-primary" />
            <span className="text-[11px] font-semibold text-verdict-primary uppercase tracking-wider">Credits & Plans</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text-primary tracking-tight mb-2">
            Power Your Research
          </h1>
          <p className="text-text-secondary text-sm max-w-md mx-auto">
            Top up your balance instantly or subscribe for maximum value
          </p>
        </div>
      </div>

      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Credit Balance */}
      {balance !== null && (
        <SpotlightCard className="rounded-2xl" contentClassName="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15">
                <Coins className="w-5 h-5 text-verdict-primary" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1">
                  Your Balance
                  <InfoTooltip content="Credits are used each time you research. Quick ~5, Standard ~25, Comprehensive ~75. Credits never expire." position="right" />
                </p>
                <p className={cn(
                  'text-2xl font-bold tabular-nums',
                  isUnlimited ? 'text-verdict-primary' :
                  (balance ?? 0) >= 1000 ? 'text-emerald-400' :
                  (balance ?? 0) >= 200 ? 'text-amber-400' : 'text-red-400'
                )}>
                  {isUnlimited ? 'Unlimited' : `${(balance ?? 0).toLocaleString()} credits`}
                </p>
              </div>
            </div>
            {!isUnlimited && (
              <button
                onClick={() => document.getElementById('credit-packs')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-verdict-primary to-verdict-primary-hover text-black hover:shadow-[0_4px_20px_rgba(212,165,116,0.3)] transition-all duration-200"
              >
                <Sparkles className="w-4 h-4" />
                Top Up
              </button>
            )}
          </div>
        </SpotlightCard>
      )}

      {/* Credit Packs */}
      {packs.length > 0 && !isUnlimited && (
        <div id="credit-packs">
          <div className="text-center mb-5">
            <h2 className="text-base font-serif font-semibold text-text-primary flex items-center justify-center gap-2">
              <Coins className="w-4 h-4 text-verdict-primary" />
              Quick Top-Up
            </h2>
            <p className="text-text-muted text-xs mt-1">No subscription required. Buy once, use anytime.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[...packs].sort((a, b) => a.price_usd - b.price_usd).map((pack) => {
              const bestValuePack = packs.reduce((best, p) =>
                p.bonus_percent > best.bonus_percent ? p : best, packs[0]);
              const isBestValue = pack.id === bestValuePack?.id && pack.bonus_percent > 0;
              const isThisPackLoading = purchasingPack === pack.id;
              return (
                <div key={pack.id} className={cn('relative', isBestValue && 'pt-3.5')}>
                  {isBestValue && (
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                      <span className="px-3 py-0.5 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-widest rounded-md shadow-lg shadow-emerald-500/25">
                        Best Value
                      </span>
                    </div>
                  )}
                  <SpotlightCard
                    className={cn(
                      'rounded-2xl cursor-pointer',
                      isBestValue && 'ring-1 ring-emerald-500/40',
                      isThisPackLoading && 'opacity-70',
                      purchasingPack && !isThisPackLoading && 'opacity-50 pointer-events-none'
                    )}
                    contentClassName="p-5 text-center"
                  >
                    <div onClick={() => handleBuyCredits(pack.id)}>
                      {isThisPackLoading ? (
                        <div className="py-4">
                          <Loader2 className="w-6 h-6 text-verdict-primary animate-spin mx-auto mb-2" />
                          <p className="text-sm text-text-secondary">Redirecting to checkout...</p>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-semibold text-text-primary text-sm mb-1.5">{pack.name}</h3>
                          <p className="text-2xl font-bold text-text-primary mb-1">${pack.price_usd}</p>
                          <p className="text-verdict-primary font-semibold tabular-nums">
                            {pack.credits.toLocaleString()} credits
                          </p>
                          {pack.bonus_percent > 0 && (
                            <p className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-[11px] font-medium">
                              <Sparkles className="w-3 h-3" />
                              +{pack.bonus_percent}% bonus
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </SpotlightCard>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Current Plan + Billing Toggle */}
      {currentTier && (
        <div className="space-y-5">
          <SpotlightCard className="rounded-2xl" contentClassName="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-5">
                <div>
                  <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider mb-1">Current Plan</p>
                  <p className="font-semibold text-base text-text-primary">
                    <TierDisplay tier={currentTier.tier} />
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider mb-1">Status</p>
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border',
                    (subscription?.status === 'active' || isMasterOrEnterprise)
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-verdict-primary/10 text-verdict-primary border-verdict-primary/20'
                  )}>
                    {isMasterOrEnterprise ? 'Active' : subscription?.status === 'active' ? 'Active' : 'Pay-as-you-go'}
                  </span>
                </div>
              </div>
              {subscription?.status === 'active' && !isMasterOrEnterprise && (
                <button
                  onClick={handleManageSubscription}
                  disabled={checkoutLoading === 'manage'}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all',
                    'bg-surface-light border border-border-subtle text-text-primary hover:bg-surface-light hover:text-text-primary',
                    checkoutLoading === 'manage' && 'opacity-70 cursor-not-allowed'
                  )}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  {checkoutLoading === 'manage' ? 'Loading...' : 'Manage Subscription'}
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </button>
              )}
            </div>
          </SpotlightCard>

          {/* Billing Toggle */}
          {!isMasterOrEnterprise && (
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-surface-light/50 border border-border-subtle">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={cn(
                    'px-5 py-2 rounded-xl text-sm font-medium transition-all duration-300',
                    billingPeriod === 'monthly'
                      ? 'bg-gradient-to-b from-verdict-primary/20 to-verdict-primary/10 text-verdict-primary border border-verdict-primary/25'
                      : 'text-text-muted hover:text-text-primary'
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className={cn(
                    'px-5 py-2 rounded-xl text-sm font-medium transition-all duration-300',
                    billingPeriod === 'annual'
                      ? 'bg-gradient-to-b from-verdict-primary/20 to-verdict-primary/10 text-verdict-primary border border-verdict-primary/25'
                      : 'text-text-muted hover:text-text-primary'
                  )}
                >
                  Annual
                  <span className={cn(
                    'ml-1.5 text-[10px] font-bold uppercase tracking-wider',
                    billingPeriod === 'annual' ? 'text-emerald-400' : 'text-emerald-500/70'
                  )}>Save 17%</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Master/Enterprise */}
      {isMasterOrEnterprise ? (
        <SpotlightCard className="rounded-2xl" contentClassName="py-8 px-6 text-center">
          <div className="p-3 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-2xl border border-verdict-primary/15 mx-auto w-fit mb-4">
            <Shield className="w-10 h-10 text-verdict-primary" />
          </div>
          <h2 className="text-lg font-serif font-bold text-text-primary mb-2">
            {currentTier?.tier === 'master' ? 'Master' : 'Enterprise'} Access
          </h2>
          <p className="text-text-secondary text-sm max-w-md mx-auto">
            Full access to all features with no limits.
          </p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {[
              { value: 'Unlimited', label: 'Credits' },
              { value: 'Unlimited', label: 'File uploads' },
              { value: 'All', label: 'AI Models' },
              { value: 'Unlimited', label: 'Web sources' },
            ].map((stat) => (
              <div key={stat.label} className="p-3 rounded-xl bg-surface-light/50 border border-border-subtle">
                <p className="text-lg font-bold text-verdict-primary">{stat.value}</p>
                <p className="text-[11px] text-text-muted uppercase tracking-wider mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </SpotlightCard>
      ) : (
        /* Subscription Tier Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {availableTiers.map((tier) => {
            const Icon = tierIcons[tier.tier] || Star;
            const isCurrent = currentTier?.tier === tier.tier;
            const isPopular = tier.tier === 'partner' || tier.tier === 'standard';
            const isTeam = tier.tier === 'team' || tier.tier === 'professional';
            const priceInfo = formatPrice(tier);
            const isUnavailable = !tier.enabled;
            const canSelect = !isCurrent && !isUnavailable && tier.tier !== 'trial';
            const currentTierRank = TIER_HIERARCHY[currentTier?.tier || 'trial'] ?? 0;
            const targetTierRank = TIER_HIERARCHY[tier.tier] ?? 0;
            const isUpgrade = targetTierRank > currentTierRank;
            const creditInfo = TIER_CREDIT_INFO[tier.tier];
            const hasBadge = isCurrent || (isPopular && !isUnavailable && !isCurrent) || (isTeam && !isCurrent) || isUnavailable;

            return (
              <div key={tier.tier} className={cn('relative', hasBadge && 'pt-4')}>
                {isCurrent && (
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-3 py-0.5 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-widest rounded-md shadow-lg shadow-emerald-500/25">
                      Current Plan
                    </span>
                  </div>
                )}
                {isPopular && !isUnavailable && !isCurrent && (
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-3 py-0.5 bg-verdict-primary text-black text-[10px] font-bold uppercase tracking-widest rounded-md shadow-lg shadow-verdict-primary/25">
                      Best Value
                    </span>
                  </div>
                )}
                {isTeam && !isCurrent && !isUnavailable && (
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-3 py-0.5 bg-gradient-to-r from-verdict-primary to-amber-500 text-black text-[10px] font-bold uppercase tracking-widest rounded-md shadow-lg shadow-verdict-primary/25">
                      Unlimited
                    </span>
                  </div>
                )}
                {isUnavailable && !isCurrent && (
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-3 py-0.5 bg-surface-light text-text-secondary text-[10px] font-bold uppercase tracking-widest rounded-md">
                      Coming Soon
                    </span>
                  </div>
                )}
                <SpotlightCard
                  className={cn(
                    'rounded-2xl h-full',
                    isTeam && !isCurrent && 'ring-1 ring-verdict-primary/40',
                    isPopular && !isUnavailable && !isCurrent && !isTeam && 'ring-1 ring-verdict-primary/30',
                    isCurrent && 'ring-1 ring-emerald-500/40',
                    isUnavailable && 'opacity-50'
                  )}
                  contentClassName="p-5 flex flex-col h-full"
                >
                  {/* Tier header */}
                  <div className={cn("text-center mb-4", isUnavailable && "opacity-75")}>
                    <div className={cn(
                      "p-2 rounded-xl mx-auto w-fit mb-2.5 border",
                      isCurrent
                        ? "bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/15"
                        : "bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 border-verdict-primary/15"
                    )}>
                      <Icon className={cn("w-5 h-5", isCurrent ? "text-emerald-400" : "text-verdict-primary")} />
                    </div>
                    <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                      {tierLabels[tier.tier] || tier.tier}
                    </h3>
                    <div className="mt-2">
                      {isUnavailable ? (
                        <span className="text-lg font-bold text-text-muted">--</span>
                      ) : (
                        <div>
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            {priceInfo.originalPrice && (
                              <span className="text-sm text-text-muted line-through">{priceInfo.originalPrice}</span>
                            )}
                            <span className="text-2xl font-bold text-text-primary">{priceInfo.price}</span>
                            <span className="text-text-muted text-xs">{priceInfo.period}</span>
                          </div>
                          {priceInfo.annualTotal && (
                            <p className="text-[11px] text-emerald-400 font-medium mt-0.5">{priceInfo.annualTotal}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Credit info */}
                  {creditInfo && !isUnavailable && (
                    <div className="mb-4 p-3 rounded-xl bg-surface-light/50 border border-border-subtle">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-text-muted">Monthly credits</span>
                        <span className={cn(
                          "font-semibold tabular-nums",
                          creditInfo.isUnlimited ? "text-verdict-primary" : "text-verdict-primary"
                        )}>
                          {creditInfo.credits}
                        </span>
                      </div>
                      {!creditInfo.isUnlimited && (
                        <div className="flex items-center justify-between text-xs mt-1.5">
                          <span className="text-text-muted">Rollover cap</span>
                          <span className="font-medium text-emerald-400 tabular-nums">{creditInfo.rolloverCap}</span>
                        </div>
                      )}
                      {!creditInfo.isUnlimited && creditInfo.creditsPerDollar > BASE_CREDITS_PER_DOLLAR && (
                        <div className="mt-2 pt-2 border-t border-border-subtle">
                          <p className="text-emerald-400 text-[11px] font-medium flex items-center justify-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            +{Math.round(((creditInfo.creditsPerDollar - BASE_CREDITS_PER_DOLLAR) / BASE_CREDITS_PER_DOLLAR) * 100)}% vs pay-as-you-go
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Features */}
                  <ul className={cn("space-y-2 mb-5 text-xs flex-1", isUnavailable && "opacity-75")}>
                    {tier.features.slice(0, 5).map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-verdict-primary flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {isCurrent ? (
                    <div className="w-full py-2.5 px-4 rounded-xl bg-emerald-500/10 text-emerald-400 text-center text-sm font-semibold border border-emerald-500/20">
                      <Check className="w-4 h-4 inline mr-1.5" />
                      Your Current Plan
                    </div>
                  ) : (
                    <button
                      className={cn(
                        'w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1.5',
                        canSelect && isUpgrade
                          ? 'bg-gradient-to-r from-verdict-primary to-verdict-primary-hover text-black hover:shadow-[0_4px_20px_rgba(212,165,116,0.3)]'
                          : tier.tier === 'trial' || isUnavailable
                            ? 'bg-surface-light border border-border-subtle text-text-muted cursor-not-allowed'
                            : 'bg-surface-light border border-border-subtle text-text-primary hover:bg-surface-light hover:text-text-primary',
                        checkoutLoading !== null && 'opacity-70 cursor-not-allowed'
                      )}
                      disabled={tier.tier === 'trial' || isUnavailable || checkoutLoading !== null}
                      onClick={() => handleSelectPlan(tier.tier)}
                    >
                      {checkoutLoading === tier.tier ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</>
                      ) : isUnavailable ? (
                        'Coming Soon'
                      ) : isUpgrade ? (
                        'Upgrade Now'
                      ) : (
                        'Downgrade'
                      )}
                    </button>
                  )}
                </SpotlightCard>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-5 text-center space-y-3">
        <p className="font-semibold text-text-primary text-sm">All paid plans include</p>
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
          {['Access to ALL AI models', 'Credit rollover', 'Multi-agent fact-checking', 'Cancel anytime'].map((feature) => (
            <span key={feature} className="flex items-center gap-1.5 text-text-secondary text-sm">
              <Check className="w-3.5 h-3.5 text-verdict-primary flex-shrink-0" />
              {feature}
            </span>
          ))}
        </div>
        <p className="text-text-muted text-xs">
          Questions?{' '}
          <a href="mailto:contact@vettis.ai" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors">
            contact@vettis.ai
          </a>
        </p>
      </SpotlightCard>

    </div>
  );
}
